import React, { useState, useEffect } from 'react';
import { AuthenticationDetails, CognitoUser } from 'amazon-cognito-identity-js';
import { userPool } from '../services/cognitoConfig'; 
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { User } from '../types/types';
import Container from '@mui/material/Container';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import logo from '../assets/images/readLinker_mini_logo_128.png';
import '../css/LoginPage.css';

// 白背景の入力欄をダーク基調のページ上に置くための共通スタイル。
// ラベル位置はMUIの既定計算（notched outline）に任せ、独自のtransformは入れない。
const loginFieldSx = {
    '& .MuiOutlinedInput-root': {
        backgroundColor: 'white',
        borderRadius: 1,
    },
    '& .MuiOutlinedInput-input': {
        color: 'rgba(0, 0, 0, 0.87)',
    },
    // Chromeのパスワード自動入力による青みがかった背景・文字色の上書きを防ぐ
    '& input:-webkit-autofill': {
        WebkitBoxShadow: '0 0 0 1000px white inset',
        WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)',
    },
    '& .MuiInputLabel-root': {
        backgroundColor: 'white',
        padding: '0 4px',
        color: 'rgba(0, 0, 0, 0.6)',
        '&.Mui-focused': {
            color: 'rgba(0, 0, 0, 0.6)',
        },
    },
};

const LoginPage: React.FC = () => {
    useEffect(() => {
        document.body.classList.add('login-page');
        return () => {
            document.body.classList.remove('login-page');
        };
    }, []);

    const [identifier, setIdentifier] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [loginFailed, setLoginFailed] = useState(false);

    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const infoMessage = (location.state as { message?: string } | null)?.message ?? null;

    const handleIdentifierChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (/\s/.test(value)) {
            setError('空白文字は使用できません。');
        } else {
            setError(null);
            setIdentifier(value);
        }
    };

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (/\s/.test(value)) {
            setError('空白文字は使用できません。');
        } else {
            setError(null);
            setPassword(value);
        }
    };

    const onSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        setError(null);
        setLoginFailed(false);

        if (!identifier || !password) {
            setError('ユーザー名とパスワードを入力してください。');
            return;
        }

        const authenticationDetails = new AuthenticationDetails({
            Username: identifier,
            Password: password,
        });

        const cognitoUser = new CognitoUser({
            Username: identifier,
            Pool: userPool,
        });

        cognitoUser.authenticateUser(authenticationDetails, {
            onSuccess: (session) => {
                // console.log('ログイン成功', session);
                const idToken = session.getIdToken().getJwtToken();
                const refreshToken = session.getRefreshToken().getToken();
                sessionStorage.setItem("idToken", idToken);
                sessionStorage.setItem("refreshToken", refreshToken);
                // console.log('idToken:', idToken);

                const userInfo: User = {
                    // "name" クレームではなく "cognito:username" を正とする
                    // （ログインIDと一致しない場合があり、アカウント切替機能で使う
                    // CognitoUser の再構築にはこちらが必要なため）
                    username: session.getIdToken().payload['cognito:username'] || session.getIdToken().payload.name,
                    sub: session.getIdToken().payload.sub,
                    email: session.getIdToken().payload.email,
                };
                localStorage.setItem("userInfo", JSON.stringify(userInfo));
                login(userInfo);
                
                setTimeout(() => {
                    navigate('/');
                }, 100);
            },
            onFailure: (err) => {
                console.error('ログイン失敗', err);
                setError('ログインに失敗しました。ユーザー名またはパスワードが正しくありません。');
                setLoginFailed(true);
            },
        });
    };

    return (
        <div className="login-page">
            <Container maxWidth="xs">
                <Box
                    sx={{
                        minHeight: '100vh',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        color: 'white',
                        py: 20,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            textAlign: 'center',
                            width: '100%',
                        }}
                    >
                        <img src={logo} alt="ReadLinker logo" style={{ width: '128px', height: '128px' }} />
                        <Typography component="h1" variant="h5" sx={{ marginTop: 2 }}>
                            すべての本が、ここに。
                        </Typography>
                        <Typography variant="body1" sx={{ marginTop: 2 }}>
                            数万冊の蔵書管理 新しい本との出会い
                        </Typography>
                        {infoMessage && (
                            <Typography variant="body2" sx={{ color: '#8fd694', mt: 2 }}>
                                {infoMessage}
                            </Typography>
                        )}
                        <Box component="form" onSubmit={onSubmit} sx={{ mt: 3, width: '100%' }}>
                            <TextField
                                variant="outlined"
                                margin="normal"
                                required
                                fullWidth
                                id="identifier"
                                label="ユーザー名"
                                name="identifier"
                                autoComplete="identifier"
                                autoFocus
                                value={identifier}
                                onChange={handleIdentifierChange}
                                sx={loginFieldSx}
                            />
                            {error && <Typography variant="body2" sx={{ color: 'red', mt: 1 }}>{error}</Typography>}
                            {loginFailed && (
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                    パスワードをお忘れですか？<Link to="/forgot-password" style={{ color: 'white' }}>こちら</Link>
                                </Typography>
                            )}
                            <TextField
                                variant="outlined"
                                margin="normal"
                                required
                                fullWidth
                                name="password"
                                label="パスワード"
                                type="password"
                                id="password"
                                autoComplete="current-password"
                                value={password}
                                onChange={handlePasswordChange}
                                sx={loginFieldSx}
                            />

                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                color="primary"
                                sx={{ mt: 3, mb: 2 }}
                            >
                                ログイン
                            </Button>
                        </Box>
                        <Typography variant="body2" sx={{ marginTop: 2 }}>
                            アカウントをお持ちでない方は<Link to="/register" style={{ color: 'white' }}>こちら</Link>
                        </Typography>
                        <Typography variant="body2" sx={{ marginTop: 1, color: 'rgba(255,255,255,0.7)' }}>
                            書誌登録はモバイルアプリが便利です
                        </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ textAlign: 'center' }}>
                        © 2023-2026 ADA LLC.
                    </Typography>
                </Box>
            </Container>
        </div>
    );
};

export default LoginPage;
